import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { InlineMessage } from './ui';

export interface DiscoveryIntegration {
  id: string;
  platform?: string;
  storeName?: string;
  name?: string;
}

interface Suggestion {
  integrationId: string;
  platform: 'shopify' | 'lightspeed';
  externalId: string;
  name: string;
  address: { street: string; city: string; state: string; zip: string };
  state: 'new' | 'matched' | 'linked';
  matchedBy?: 'address' | 'name';
  locationId: string | null;
  locationName: string | null;
  canMapZone: boolean;
}

interface DiscoveryResponse {
  suggestions: Suggestion[];
  scopeStatus?: 'reconnect_required';
  error?: string;
}

const readMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

const addressLine = (a: Suggestion['address']): string =>
  [a.street, [a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' · ');

interface SourceProps {
  integration: DiscoveryIntegration;
  onAccepted: () => void;
  /** Reports whether this source has anything to show, so the list can hold back its heading */
  onVisibleChange: (integrationId: string, visible: boolean) => void;
}

/** Suggestions for one connected integration. Hidden when there is nothing to show. */
const DiscoverySource: React.FC<SourceProps> = ({ integration, onAccepted, onVisibleChange }) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  // Per-suggestion override of the zone checkbox; unset follows the default (checked only for an address match)
  const [zoneChoice, setZoneChoice] = useState<Record<string, boolean>>({});
  const zoneChecked = (s: Suggestion) => zoneChoice[s.externalId] ?? !(s.state === 'matched' && s.matchedBy === 'name');
  const key = ['location-discovery', integration.id];
  const label = integration.storeName || integration.name || integration.id;

  const discovery = useQuery({
    queryKey: key,
    retry: false,
    queryFn: async () => {
      const response = await makeApiCall(`/store/locations/discover?integrationId=${encodeURIComponent(integration.id)}`, {
        headers: await getAuthHeaders(),
      });
      // Not an owner or admin, or the flag is off for this store: show nothing
      if (!response.ok) return null;
      return ((await response.json()).data || null) as DiscoveryResponse | null;
    },
  });

  const accept = useMutation({
    mutationFn: async (s: Suggestion) => {
      const response = await makeApiCall('/store/locations/discover/accept', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          integrationId: s.integrationId,
          externalId: s.externalId,
          ...(s.state === 'matched' && s.locationId ? { locationId: s.locationId } : {}),
          mapZone: s.canMapZone && zoneChecked(s),
        }),
      });
      if (!response.ok) throw new Error(await readMessage(response, t('integrations.locationDiscovery.actionFailed')));
      return (await response.json()).data as { zoneError?: string };
    },
    onSuccess: (data) => {
      setNotice(data?.zoneError ? t('integrations.locationDiscovery.zoneFailed', { message: data.zoneError }) : null);
      queryClient.invalidateQueries({ queryKey: key });
      onAccepted();
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const dismiss = useMutation({
    mutationFn: async (s: Suggestion) => {
      const response = await makeApiCall('/store/locations/discover/dismiss', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ integrationId: s.integrationId, externalId: s.externalId }),
      });
      if (!response.ok) throw new Error(await readMessage(response, t('integrations.locationDiscovery.actionFailed')));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (e: Error) => setNotice(e.message),
  });

  const data = discovery.data;
  const open = (data?.suggestions || []).filter((s) => s.state !== 'linked');
  const visible = !!data && (!!data.scopeStatus || open.length > 0 || !!notice);
  useEffect(() => {
    onVisibleChange(integration.id, visible);
  }, [integration.id, visible, onVisibleChange]);
  if (!data || !visible) return null;

  return (
    <Stack spacing={1}>
      {data.scopeStatus === 'reconnect_required' && (
        <InlineMessage severity="warning">{t('integrations.locationDiscovery.reconnect', { integration: label })}</InlineMessage>
      )}
      {data.error && <InlineMessage severity="info">{t('integrations.locationDiscovery.readFailed', { integration: label })}</InlineMessage>}
      {notice && <InlineMessage severity="warning">{notice}</InlineMessage>}
      {open.map((s) => (
        <Box key={`${s.integrationId}:${s.externalId}`} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body1" fontWeight={600}>{s.name}</Typography>
              <Chip
                size="small"
                variant="outlined"
                color={s.state === 'matched' ? 'primary' : 'default'}
                label={s.state === 'matched' ? t('integrations.locationDiscovery.matched', { name: s.locationName }) : t('integrations.locationDiscovery.new')}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {[t('integrations.locationDiscovery.source', { platform: s.platform === 'shopify' ? 'Shopify' : 'Lightspeed', integration: label }), addressLine(s.address)].filter(Boolean).join(' · ')}
            </Typography>
            {s.canMapZone && (
              <FormControlLabel
                control={<Checkbox size="small" checked={zoneChecked(s)} onChange={(e) => setZoneChoice((z) => ({ ...z, [s.externalId]: e.target.checked }))} />}
                label={<Typography variant="body2">{t('integrations.locationDiscovery.mapZone')}</Typography>}
              />
            )}
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button size="small" variant="contained" disabled={accept.isPending} onClick={() => accept.mutate(s)}>
              {s.state === 'matched' ? t('integrations.locationDiscovery.acceptLink', { name: s.locationName }) : t('integrations.locationDiscovery.accept')}
            </Button>
            <Button size="small" disabled={dismiss.isPending} onClick={() => dismiss.mutate(s)}>
              {t('integrations.locationDiscovery.dismiss')}
            </Button>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

interface ListProps {
  integrations: DiscoveryIntegration[];
  /** Called after a suggestion is accepted so the locations list reloads */
  onAccepted: () => void;
}

/**
 * "Found on your account": the locations a store's connected Shopify and
 * Lightspeed integrations already know about, offered to add or link. Owner
 * and admin only (the server refuses everyone else, and the list then stays
 * empty) and behind `location_discovery`.
 */
export const LocationDiscoveryList: React.FC<ListProps> = ({ integrations, onAccepted }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('location_discovery');
  const sources = integrations.filter((i) => i.platform === 'shopify' || i.platform === 'lightspeed');
  // Sources stay mounted so they can load; the heading shows only once one has something to show
  const [visibleSources, setVisibleSources] = useState<Record<string, boolean>>({});
  const onVisibleChange = useCallback((id: string, visible: boolean) => {
    setVisibleSources((v) => (v[id] === visible ? v : { ...v, [id]: visible }));
  }, []);
  if (!enabled || sources.length === 0) return null;
  const anyVisible = sources.some((i) => visibleSources[i.id]);
  return (
    <Box sx={{ mb: anyVisible ? 2 : 0 }}>
      {anyVisible && (
        <>
          <Typography variant="subtitle2">{t('integrations.locationDiscovery.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('integrations.locationDiscovery.subtitle')}
          </Typography>
        </>
      )}
      <Stack spacing={1.5}>
        {sources.map((i) => (
          <DiscoverySource key={i.id} integration={i} onAccepted={onAccepted} onVisibleChange={onVisibleChange} />
        ))}
      </Stack>
    </Box>
  );
};

export default LocationDiscoveryList;
